import { createContext, useState, useContext, useEffect, useCallback } from 'react';
import * as api from '../services/api';

const AuthContext = createContext();

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Check auth status on mount
    useEffect(() => {
        checkAuth();

        // Listen for logout events (from token refresh failure)
        const handleLogout = () => {
            setUser(null);
        };
        window.addEventListener('auth:logout', handleLogout);
        return () => window.removeEventListener('auth:logout', handleLogout);
    }, []);

    const checkAuth = useCallback(async () => {
        if (!api.isAuthenticated()) {
            setLoading(false);
            return;
        }

        try {
            const result = await api.getMe();
            if (result.success) {
                setUser(result.user);
            } else {
                setUser(null);
            }
        } catch (err) {
            console.error('Auth check failed:', err);
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    const login = useCallback(async (email, password) => {
        setError(null);
        try {
            const result = await api.login(email, password);
            if (result.success) {
                setUser(result.user);
                return { success: true };
            } else {
                setError(result.error);
                return { success: false, error: result.error };
            }
        } catch (err) {
            const errorMsg = 'Login failed. Please try again.';
            setError(errorMsg);
            return { success: false, error: errorMsg };
        }
    }, []);

    const register = useCallback(async (email, password, nickname) => {
        setError(null);
        try {
            const result = await api.register(email, password, nickname);
            if (result.success) {
                setUser(result.user);
                return { success: true };
            } else {
                setError(result.error);
                return { success: false, error: result.error };
            }
        } catch (err) {
            const errorMsg = 'Registration failed. Please try again.';
            setError(errorMsg);
            return { success: false, error: errorMsg };
        }
    }, []);

    const logout = useCallback(async () => {
        try {
            await api.logout();
        } finally {
            setUser(null);
        }
    }, []);

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    const value = {
        user,
        isLoggedIn: !!user,
        isAdmin: user?.role === 'admin',
        loading,
        error,
        login,
        register,
        logout,
        clearError,
        checkAuth,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
